import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { dayjs } from '@mobi/utils/date'
import * as iconPaths from '@mobi/component-library/Common/Icon/iconPaths'
import { Icon } from '@mobi/component-library/Common/Icon'
import { GridCell } from '@mobi/component-library/Common/Grid'
import { confirmAllBets } from '@mobi/betslip/Store/Workflow/asyncActions'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { clearToteSportSelections } from '../../Store'
import { getPool, queryDefaults, ToteSportsPool } from '../../data/toteSports'
import { NoticeBox } from '@core/Components/NoticeBox'
import { Money } from '@core/Components/Text/Money'
import { ConfirmBetslipBetsSucceeded, QuickbetClosed } from '@core/Areas/Quickbet/signals'
import { state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { startListening } from '@core/Store/listenerMiddleware'
import { TippingBettingDrawer } from '../BettingDrawer/BettingDrawer'
import { LoadingBars } from '../Common/LoadingBars'
import { selectTippingSelections } from '../../Store/selectors'
import {
  IconStyled,
  InfoHeaderStyled,
  InfoTitleStyled,
  JackpotStyled,
  PoolNameStyled,
} from './Components/Common/Games.styles'
import { ClosedGame } from './Components/ClosedGame'
import { GameSelections } from './Components/GameSelections'
import { Disclaimers } from './Components/Disclaimers'

interface GamesParams extends Record<string, string> {
  poolKey: string
  sportIconCode: string
}

const DATE_TIME_FORMAT = 'ddd, DD MMM YYYY HH:mm'

export const Games = (): JSX.Element => {
  const { poolKey, sportIconCode } = useParams<GamesParams>() as GamesParams
  const selections = useSelector(selectTippingSelections)
  const dispatch = useDispatch()

  React.useEffect(() => {
    const clearAllSelections = () => {
      dispatch(clearToteSportSelections())
    }

    const quickbetSubscription = QuickbetClosed.signal$
      .withLatestFrom(quickbetState$, (_: unknown, quickbetState) => ({ quickbetState }))
      .subscribe(({ quickbetState }) => {
        if (!quickbetState.get('betPlaced')) return
        clearAllSelections()
      })

    const betslipSubscription = ConfirmBetslipBetsSucceeded.signal$.subscribe(clearAllSelections)

    const newBetSlipDisposeSubscription = startListening({
      actionCreator: confirmAllBets.fulfilled,
      effect: clearAllSelections,
    })

    return () => {
      clearAllSelections()
      newBetSlipDisposeSubscription()
      quickbetSubscription.dispose()
      betslipSubscription.dispose()
    }
  }, [dispatch])

  const {
    isLoading,
    isError,
    data: pool,
  } = useQuery<ToteSportsPool>({
    ...queryDefaults,
    queryKey: [queryKeys.toteSportsPool, poolKey],
    queryFn: () => getPool(poolKey),
  })

  if (isLoading) {
    return <LoadingBars />
  }

  if (isError || !pool) {
    return (
      <GridCell padding='1rem'>
        <NoticeBox
          title='Unable to display games at this time. Please try again later.'
          noticeBoxType='Info'
          hasBorder
        />
      </GridCell>
    )
  }

  const icon = sportIconCode?.toUpperCase() as keyof typeof iconPaths
  const scheduledCloseTime = dayjs(pool.scheduledCloseTime).format(DATE_TIME_FORMAT)
  const hasDraw = pool.games.some(game => game.isDrawSupported)

  // releaseTime is typed as a date, but sometimes is actually a string.
  // Creating a new date from the value ensures that it's always a date object.
  const releaseTime = pool.releaseTime ? new Date(pool.releaseTime) : null
  const isRefunded = releaseTime != null && pool.isAbandoned
  const formattedReleaseTime = releaseTime ? dayjs(pool.releaseTime).format(DATE_TIME_FORMAT) : ''

  return (
    <>
      <InfoHeaderStyled>
        <PoolNameStyled data-tid-tote-sport-pool-name>
          <IconStyled type={icon === 'FB' ? 'FBL' : icon || 'tab'} />
          {`${pool.name} ${pool.nameSuffix} ${pool.numberOfGames}`}
        </PoolNameStyled>

        <div data-tid-tote-sport-pool-amount>
          <span>Pool:&nbsp;</span>
          <Money amount={Math.floor(pool.total)} decimalPlaces={0} />
          {pool.grossJackpot && (
            <JackpotStyled>
              <Icon type={'jackpot'} data-tid-tote-sport-pool-jackpot-icon='' />
              <Money amount={Math.floor(pool.grossJackpot)} decimalPlaces={0} />
            </JackpotStyled>
          )}
        </div>

        {pool.isOpen && (
          <div data-tid-tote-sport-pool-scheduled-close-time>
            Betting Closes: {scheduledCloseTime}
          </div>
        )}
        {isRefunded && (
          <div data-tid-tote-sport-refund-time>Refunds Official: {formattedReleaseTime}</div>
        )}
        {releaseTime != null && !isRefunded && (
          <>
            <div data-tid-tote-sport-tips-required>
              Tips Required: {pool.dividend?.numberOfTipsRequired || 0}/{pool.numberOfGames}
            </div>
            <div data-tid-tote-sport-dividend>
              Dividend: <Money amount={pool.dividend?.return || 0} />
            </div>
            <div data-tid-tote-sports-official-time>Dividends Official: {formattedReleaseTime}</div>
          </>
        )}
      </InfoHeaderStyled>

      <InfoTitleStyled>
        <div>HOME</div>
        {hasDraw && <div>DRAW</div>}
        <div>AWAY</div>
      </InfoTitleStyled>

      {pool?.isOpen &&
        pool.games.map((game, i) => {
          const gameSelections = selections?.[game.key] || {
            isHome: false,
            isDraw: false,
            isAway: false,
          }
          return (
            <GameSelections
              key={game.key}
              game={game}
              gameIndex={i}
              isHomeSelected={gameSelections.isHome}
              isDrawSelected={gameSelections.isDraw}
              isAwaySelected={gameSelections.isAway}
            />
          )
        })}

      {pool?.isOpen === false &&
        pool.games.map((game, i) => {
          return <ClosedGame key={game.key} game={game} gameIndex={i} />
        })}

      <Disclaimers disclaimers={pool.disclaimers} />
      <TippingBettingDrawer poolKey={pool.key} />
    </>
  )
}
